import { BannerPlatform } from '@app/graphql/types/graphql';
import CDNLink from '@app/utils/cdn-link';
import { Card, GridItem, Image, SimpleGrid, Text, Box } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import useNewsBanners from '../use-news-banner';
import useStoreFrontBranch from '../use-storefront_branch-query.ts';
import NewsBannerDialog from './news-banner-dialog';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface NewsBanner {
  id: string;
  title: string;
  description?: string | null;
  startTime: string;
  endTime: string;
  isActive: boolean;
  platforms: BannerPlatform[];
  imageUrl: string;
  allowPopup: boolean;
}

interface NewsBannersResponse {
  storefront_getAvailableNewsBanner: NewsBanner[];
}

export default function NewsBanner(props: { branchID: string | null; platform: BannerPlatform }) {
  const branchID = props.branchID;

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedBannerIndex, setSelectedBannerIndex] = useState(0);

  // Auto dialog banner
  const [isAutoDialogOpen, setIsAutoDialogOpen] = useState(false);

  // Fetch branch data
  const { data: branchData, error: branchError, loading: branchLoading } = useStoreFrontBranch(branchID ?? '');
  const companyId = branchData?.storefront_branch?.companyId;

  // Fetch banners data
  const {
    data: newsBannersData,
    error: bannersError,
    loading: bannersLoading,
  } = useNewsBanners(companyId ?? '', { platforms: props.platform });

  // Handle loading and error states
  useEffect(() => {
    if (branchLoading || bannersLoading) return;
    if (branchError ?? bannersError) return;
  }, [branchLoading, bannersLoading, branchError, bannersError]);

  useEffect(() => {
    if (!newsBannersData?.storefront_getAvailableNewsBanner) return;
    const autoDialogBanners = newsBannersData.storefront_getAvailableNewsBanner.filter((banners) => banners.allowPopup);
    if (autoDialogBanners.length > 0) {
      // Check if dialog has been shown in this session
      const hasShownDialog = sessionStorage.getItem('newsBannerDialogShown');
      if (!hasShownDialog) {
        setIsAutoDialogOpen(true);
        // Mark dialog as shown in this session
        sessionStorage.setItem('newsBannerDialogShown', 'true');
      }
    }
  }, [newsBannersData?.storefront_getAvailableNewsBanner]);

  if (branchLoading || bannersLoading) return null;
  if (branchError) return <Text>Error loading branch data: {branchError.message}</Text>;
  if (bannersError) return <Text>Error loading banners data: {bannersError.message}</Text>;

  const banners = (newsBannersData as NewsBannersResponse | undefined)?.storefront_getAvailableNewsBanner ?? [];
  if (banners.length === 0) return null;

  const autoDialogBanners = banners.filter((banners) => banners.allowPopup);

  const handleCardClick = (index: number) => {
    setSelectedBannerIndex(index);
    setIsDialogOpen(true);
  };

  return (
    <>
      <Box className="news-banner" py={4} pb={-4} maxW={'7xl'}>
        <Swiper
          className="news-banner__slider"
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={16}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1.5,
            },
            768: {
              slidesPerView: 2,
            },
            2160: {
              slidesPerView: 3,
            },
          }}
          style={{
            paddingBottom: '40px', // Only add padding when there are multiple slides
          }}
        >
          {banners.map((banner, index) => (
            <SwiperSlide key={banner.id}>
              <Card.Root
                className="news-banner__item"
                maxW={{ base: 'full', sm: 'base' }}
                overflow="hidden"
                position="relative"
                shadow={'sm'}
                mx={1}
                cursor="pointer"
                onClick={() => {
                  handleCardClick(index);
                }}
                _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <Card.Body gap="2" p={0}>
                  <SimpleGrid columns={2}>
                    <GridItem p={4}>
                      <Card.Title mb={2} lineClamp={2} fontSize={'lg'}>
                        {banner.title}
                      </Card.Title>
                      <Card.Description>
                        <Text lineClamp={5}>{banner.description}</Text>
                      </Card.Description>
                    </GridItem>
                    <GridItem>
                      <Image
                        src={CDNLink(banner.imageUrl)}
                        alt={banner.title}
                        objectFit="cover"
                        height="200px"
                        width="100%"
                      />
                    </GridItem>
                  </SimpleGrid>
                </Card.Body>
              </Card.Root>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <NewsBannerDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        banners={banners}
        selectedBannerIndex={selectedBannerIndex}
      />

      {/* Auto popup */}
      <NewsBannerDialog
        isOpen={isAutoDialogOpen}
        onClose={() => {
          setIsAutoDialogOpen(false);
        }}
        banners={autoDialogBanners}
        selectedBannerIndex={0}
      />
    </>
  );
}
